import { useCallback, useState } from "react";
import { allCopyFormats } from "@/lib/symbol-formats";
import { copyText } from "@/lib/copy";

interface Props {
  symbol: string;
}

/**
 * Renders every deterministic copy format for a symbol — raw character,
 * Unicode notation, HTML entities, CSS content, JS/JSON escape, URL encoded.
 * Each row has its own copy button so the page works as a quick reference.
 */
export function SymbolFormats({ symbol }: Props) {
  const formats = allCopyFormats(symbol);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copy = useCallback(async (key: string, value: string) => {
    const ok = await copyText(value, { source: "symbol-format", symbol, formatKey: key });
    if (!ok) return;
    setCopiedKey(key);
    window.setTimeout(() => {
      setCopiedKey((k) => (k === key ? null : k));
    }, 1200);
  }, [symbol]);


  return (
    <section
      aria-labelledby="formats-heading"
      className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6 sm:pt-12"
    >
      <h2 id="formats-heading" className="font-display text-xl font-semibold sm:text-2xl">
        Copy in every format
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Every form of this character — for HTML, CSS, JavaScript, URLs and more.
        Click any value to copy.
      </p>
      <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {formats.map((f) => {
          const isCopied = copiedKey === f.key;
          return (
            <li key={f.key}>
              <button
                type="button"
                onClick={() => copy(f.key, f.value)}
                aria-label={`Copy ${f.label}: ${f.value}`}
                className="glass group flex w-full items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                    {f.label} — {f.hint}
                  </span>
                  <code className="mt-1 block break-all font-mono text-sm">
                    {f.value}
                  </code>
                </span>
                <span
                  className={`flex-none rounded border border-border bg-surface/60 px-2 py-1 text-[10px] uppercase tracking-wider ${
                    isCopied ? "text-accent" : "text-muted-foreground group-hover:text-foreground"
                  }`}
                  aria-hidden="true"
                >
                  {isCopied ? "Copied" : "Copy"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
